import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSocket,
  setConnected,
  setConnecting,
  setError,
  clearError,
  setUserDisconnected,
} from '../store/websocketSlice';
import {
  recordMonitoringMessageBatch,
} from '../store/monitoringSlice';
import monitoringMessageBuffer from '../services/MonitoringMessageBuffer';

const USE_PARSE_WORKER = process.env.REACT_APP_MONITORING_PARSE_WORKER === 'true';

// Get WebSocket URL from API URL
const getWebSocketUrl = () => {
  // Get API URL from environment or use default
  const apiUrl = process.env.REACT_APP_API_URL || '';

  // If we have a specific WebSocket URL, use that
  if (process.env.REACT_APP_WEBSOCKET_URL) {
    return process.env.REACT_APP_WEBSOCKET_URL;
  }

  // Otherwise derive from API URL
  if (apiUrl) {
    // Convert http(s):// to ws(s)://
    let wsUrl = apiUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    // Add /ws endpoint
    return `${wsUrl}/ws`;
  }

  // Fallback to default
  if (process.env.NODE_ENV === 'development') {
    console.warn('No API URL found, using default WebSocket URL');
  }
  return 'wss://ws-test.unitcloud.org';
};

// Export function for display purposes (replaces websocketService.getWebSocketUrlForDisplay)
export const getWebSocketUrlForDisplay = () => {
  return getWebSocketUrl();
};

/**
 * Global WebSocket hook that manages connection at the application level
 * Maintains connection across navigation unless explicitly disconnected by user
 */
export function useGlobalWebSocket(token) {
  const dispatch = useDispatch();
  const { connected, connecting, userDisconnected } = useSelector((state) => state.websocket);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const reconnectAttemptsRef = useRef(0); // Use local ref instead of Redux state
  const lastConnectionAttemptRef = useRef(0); // Track last connection attempt
  const connectRef = useRef(null); // Ref to store connect function
  const dispatchRef = useRef(dispatch); // Ref to store dispatch function
  const clearReconnectTimeoutRef = useRef(null); // Ref to store clearReconnectTimeout function
  const userDisconnectedRef = useRef(userDisconnected); // Track userDisconnected state

  // Advanced connection handling refs
  const pendingConnectTimeoutRef = useRef(null);
  const clearPendingConnectTimeoutRef = useRef(null);

  const messageBufferRef = useRef([]);
  const flushTimeoutRef = useRef(null);

  const parseWorkerRef = useRef(null);
  const parseWorkerAvailableRef = useRef(false);
  const parseWorkerBufferRef = useRef([]);
  const parseWorkerFlushTimeoutRef = useRef(null);

  const maxReconnectAttempts = 5;
  const reconnectInterval = 3000;
  const minConnectionInterval = 1000;
  const messageFlushInterval = 250;
  const maxBufferedMessages = 100;
  const parseWorkerFlushInterval = 25;
  const maxParseWorkerBufferedMessages = 100;

  // Keep refs current so WebSocket callbacks do not rely on stale closures.
  dispatchRef.current = dispatch;
  userDisconnectedRef.current = userDisconnected;

  const flushMessageBuffer = useCallback(() => {
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }

    if (messageBufferRef.current.length === 0 || !dispatchRef.current) {
      return;
    }

    const messagesToFlush = messageBufferRef.current;
    messageBufferRef.current = [];

    const stats = monitoringMessageBuffer.addMessages(messagesToFlush);

    if (stats.acceptedCount > 0) {
      dispatchRef.current(recordMonitoringMessageBatch(stats));
    }
  }, []);

  const queueMonitoringMessage = useCallback((message) => {
    messageBufferRef.current.push(message);

    if (messageBufferRef.current.length >= maxBufferedMessages) {
      flushMessageBuffer();
      return;
    }

    if (!flushTimeoutRef.current) {
      flushTimeoutRef.current = setTimeout(flushMessageBuffer, messageFlushInterval);
    }
  }, [flushMessageBuffer]);

  const handleParsedMonitoringMessage = useCallback((message) => {
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
      console.log('Global WebSocket: Message received:', {
        messageType: message.type || 'unknown',
        leaderId: message.leader_id,
        leaderSource: message.leader_source,
        hasData: !!message.data,
        hasThumbnailS3Key: !!(message.thumbnail_s3_key || message.data?.thumbnail_s3_key),
        messageKeys: Object.keys(message),
        fullMessage: message
      });
    }

    queueMonitoringMessage(message);
  }, [queueMonitoringMessage]);

  const parseAndQueueMonitoringMessage = useCallback((rawMessage) => {
    try {
      const message = JSON.parse(rawMessage);

      // Ensure message has a timestamp for click handling
      if (!message.timestamp) {
        message.timestamp = Date.now();
      } else if (typeof message.timestamp === 'string') {
        // Convert ISO string to milliseconds if needed
        message.timestamp = new Date(message.timestamp).getTime();
      }

      // Ensure message has a unique ID for tracking
      if (!message.id) {
        const sourcePart = message.leader_source || message.source_id || message.source?.source_id || 'unknown';
        message.id = `msg_${Date.now()}_${sourcePart}_${Math.random().toString(36).slice(2, 11)}`;
      }

      handleParsedMonitoringMessage(message);
    } catch (error) {
      console.error('Global WebSocket: Failed to parse message:', error);
    }
  }, [handleParsedMonitoringMessage]);

  const flushParseWorkerBuffer = useCallback(() => {
    if (parseWorkerFlushTimeoutRef.current) {
      clearTimeout(parseWorkerFlushTimeoutRef.current);
      parseWorkerFlushTimeoutRef.current = null;
    }

    if (!parseWorkerAvailableRef.current || !parseWorkerRef.current || parseWorkerBufferRef.current.length === 0) {
      return;
    }

    const rawMessages = parseWorkerBufferRef.current;
    parseWorkerBufferRef.current = [];

    parseWorkerRef.current.postMessage(rawMessages);
  }, []);

  const queueParseWorkerMessage = useCallback((rawMessage) => {
    parseWorkerBufferRef.current.push(rawMessage);

    if (parseWorkerBufferRef.current.length >= maxParseWorkerBufferedMessages) {
      flushParseWorkerBuffer();
      return;
    }

    if (!parseWorkerFlushTimeoutRef.current) {
      parseWorkerFlushTimeoutRef.current = setTimeout(flushParseWorkerBuffer, parseWorkerFlushInterval);
    }
  }, [flushParseWorkerBuffer]);

  // Clear reconnect timeout
  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Clear pending connect timeout (throttling mechanism)
  const clearPendingConnectTimeout = useCallback(() => {
    if (pendingConnectTimeoutRef.current) {
      clearTimeout(pendingConnectTimeoutRef.current);
      pendingConnectTimeoutRef.current = null;
    }
  }, []);

  clearPendingConnectTimeoutRef.current = clearPendingConnectTimeout;

  // Schedule throttled connect - prevents connection stacking with exponential backoff
  const scheduleThrottledConnect = useCallback((delay) => {
    if (pendingConnectTimeoutRef.current) {
      // Already have a pending connection, don't schedule another
      return;
    }

    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
      console.log(`Global WebSocket: Scheduling throttled connect in ${delay}ms`);
    }

    pendingConnectTimeoutRef.current = setTimeout(() => {
      pendingConnectTimeoutRef.current = null;
      if (connectRef.current && mountedRef.current && !userDisconnectedRef.current) {
        connectRef.current();
      }
    }, delay);
  }, []);

  // Store clearReconnectTimeout in ref
  clearReconnectTimeoutRef.current = clearReconnectTimeout;

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!token || userDisconnected) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Global WebSocket: Skipping connection - no token or user disconnected');
      }
      return;
    }

    // Prevent multiple simultaneous connection attempts
    if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Global WebSocket: Connection already in progress, skipping');
      }
      return;
    }

    // Prevent immediate reconnection to unhealthy socket
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Global WebSocket: Connection already open, skipping');
      }
      return;
    }

    // Throttle connection attempts using scheduled approach
    const now = Date.now();
    const timeSinceLastAttempt = now - lastConnectionAttemptRef.current;
    if (timeSinceLastAttempt < minConnectionInterval) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Global WebSocket: Throttling connection attempt, waiting ${minConnectionInterval - timeSinceLastAttempt}ms`);
      }
      scheduleThrottledConnect(minConnectionInterval - timeSinceLastAttempt);
      return;
    }
    lastConnectionAttemptRef.current = now;
    clearPendingConnectTimeout(); // Clear any pending throttled attempts

    try {
      dispatch(setConnecting(true));
      dispatch(clearError());

      // Close existing connection if any
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      // Create WebSocket URL with token parameter
      const wsUrl = `${getWebSocketUrl()}?token=${encodeURIComponent(token)}`;
      if (process.env.NODE_ENV === 'development') {
        console.log('Global WebSocket: Connecting to', getWebSocketUrl()); // Don't log token
      }

      wsRef.current = new WebSocket(wsUrl);
      dispatch(setSocket(wsRef.current));

      wsRef.current.onopen = (event) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('Global WebSocket: Connection established successfully', {
            readyState: wsRef.current.readyState,
            url: wsRef.current.url.replace(/token=[^&]+/, 'token=***'),
            protocol: wsRef.current.protocol
          });
        }
        dispatch(setConnected(true));
        dispatch(setConnecting(false));
        dispatch(clearError());
        reconnectAttemptsRef.current = 0; // Reset local counter
      };

      wsRef.current.onmessage = (event) => {
        if (USE_PARSE_WORKER && parseWorkerRef.current) {
          queueParseWorkerMessage(event.data);
        } else {
          parseAndQueueMonitoringMessage(event.data);
        }
      };

      wsRef.current.onclose = (event) => {
        flushParseWorkerBuffer();
        flushMessageBuffer();
        clearPendingConnectTimeout();

        // FOCUSED DEBUG: Only log WebSocket disconnections during navigation
        console.warn('🔌 WEBSOCKET DISCONNECT:', {
          code: event.code,
          reason: event.reason,
          page: window.location.pathname,
          time: new Date().toLocaleTimeString()
        });
        dispatch(setConnected(false));
        dispatch(setConnecting(false));
        wsRef.current = null;

        // Handle specific error codes
        if (event.code === 1006) {
          if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
            console.warn('Global WebSocket: Abnormal closure (1006) - possible authentication or network issue');
          }
        }

        // Only attempt reconnection if component is still mounted, not a clean close,
        // user hasn't manually disconnected, and we haven't exceeded max attempts
        if (mountedRef.current &&
            event.code !== 1000 && // Normal closure
            event.code !== 1001 && // Going away
            !userDisconnectedRef.current &&
            reconnectAttemptsRef.current < maxReconnectAttempts) {

          const currentAttempt = reconnectAttemptsRef.current;
          // Exponential backoff with jitter for 1006 errors
          const baseDelay = event.code === 1006 ? reconnectInterval * 2 : reconnectInterval;
          const delay = baseDelay * Math.pow(1.5, currentAttempt) + Math.random() * 1000;

          if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
            console.log(`Global WebSocket: Reconnecting in ${Math.round(delay)}ms (attempt ${currentAttempt + 1}/${maxReconnectAttempts})`);
          }

          // Use scheduleThrottledConnect for improved reconnection management
          scheduleThrottledConnect(delay);
          reconnectAttemptsRef.current = currentAttempt + 1;
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          const errorMsg = event.code === 1006
            ? 'WebSocket connection failed - check authentication and network connectivity'
            : `Failed to reconnect after ${maxReconnectAttempts} attempts`;
          dispatch(setError(errorMsg));
        }
      };

      wsRef.current.onerror = (event) => {
        console.error('Global WebSocket: Connection error:', event);
        dispatch(setError('WebSocket connection error'));
        dispatch(setConnecting(false));
      };

    } catch (error) {
      console.error('Global WebSocket: Failed to create connection:', error);
      dispatch(setError(`Connection failed: ${error.message}`));
      dispatch(setConnecting(false));
    }
  }, [token, userDisconnected, dispatch, scheduleThrottledConnect, clearPendingConnectTimeout]);

  // Store functions in refs to break circular dependencies
  connectRef.current = connect;
  dispatchRef.current = dispatch;

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    clearReconnectTimeout();
    clearPendingConnectTimeout();
    dispatch(setUserDisconnected(true));

    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }

    dispatch(setConnected(false));
    dispatch(setConnecting(false));
    dispatch(clearError());
  }, [dispatch, clearReconnectTimeout, clearPendingConnectTimeout]);

  // Reconnect (user initiated)
  const reconnect = useCallback(() => {
    if (dispatchRef.current) {
      dispatchRef.current(setUserDisconnected(false));
    }
    reconnectAttemptsRef.current = 0; // Reset local counter
    // Use setTimeout to ensure state update is processed before connecting
    setTimeout(() => {
      if (connectRef.current) {
        connectRef.current();
      }
    }, 0);
  }, []); // No dependencies - use refs to avoid circular dependency

  // Auto-connect when token is available and user hasn't manually disconnected
  // Also handles reconnection when userDisconnected changes from true to false
  useEffect(() => {
    if (token && !userDisconnected && !connected && !connecting && connectRef.current) {
      connectRef.current();
    }
  }, [token, userDisconnected, connected, connecting]); // Use ref to avoid circular dependency

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      clearReconnectTimeout();
      clearPendingConnectTimeout();
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmount');
      }
    };
  }, [clearReconnectTimeout, clearPendingConnectTimeout]);

  // Create stable function references that don't change on every render
  const stableConnect = useCallback(() => {
    if (connectRef.current) {
      connectRef.current();
    }
  }, []); // No dependencies - always stable

  const stableDisconnect = useCallback(() => {
    if (clearReconnectTimeoutRef.current) {
      clearReconnectTimeoutRef.current();
    }

    if (dispatchRef.current) {
      dispatchRef.current(setUserDisconnected(true));
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }

    if (dispatchRef.current) {
      dispatchRef.current(setConnected(false));
      dispatchRef.current(setConnecting(false));
      dispatchRef.current(clearError());
    }
  }, []); // No dependencies - always stable using refs

  return {
    connect: stableConnect, // Stable reference for manual reconnection
    disconnect: stableDisconnect, // Stable reference for disconnection
    connected,
    connecting,
    userDisconnected,
    reconnectAttempts: reconnectAttemptsRef.current, // Return current value from ref
    scheduleThrottledConnect, // Advanced connection handling
    clearPendingConnectTimeout, // Clear throttled connection timeouts
  };
}
